/**
 * Dev-only Diagnostic Utility for Phase 1b Visual Capture.
 *
 * Tracks the exact lifecycle of visual capture:
 * - Selected Source (model / krpano / none)
 * - Model ID & Metadata
 * - Capture Switch (enable_visual_capture: DB value, Force ON, Force OFF)
 * - Timestamps for: Request -> Captured -> Submitted to client -> Sent qua socket
 * - Skip / Error reasons
 * - Thumbnail of EXACT frame submitted to sendRealtimeInput
 * - Export: Download JPEG frame & capture-events.json
 *
 * CRITICAL PRIVACY & SECURITY RULES:
 * - Only active in development and when explicitly requested via ?visualCaptureDebug=1
 * - NEVER logs base64, audio, or API keys to console
 * - In capture-events.json export, base64 data and secrets are strictly omitted
 */

export interface VisualCaptureEvent {
  id: string;
  timestamp: number;
  timeIso: string;
  stage: 'request' | 'captured' | 'submitted_to_client' | 'socket_sent' | 'skipped' | 'error';
  sourceId: 'three-model-viewer' | 'krpano' | 'none';
  modelId?: string | null;
  reason?: string;
  encodedBytes?: number;
  width?: number;
  height?: number;
  clientStatus?: string;
  details?: Record<string, unknown>;
}

export type ForceCaptureMode = 'auto' | 'force_off';

export interface VisualCaptureDiagnosticState {
  isDebugEnabled: boolean;
  selectedSourceId: 'three-model-viewer' | 'krpano' | 'none';
  activeModelId: string | null;
  sourceStatus: 'idle' | 'ready' | 'loading' | 'capturing' | 'skipped' | 'error';
  dbCaptureEnabled: boolean;
  forceMode: ForceCaptureMode;
  effectiveCaptureEnabled: boolean;
  requestTimestamp: number | null;
  capturedTimestamp: number | null;
  submittedTimestamp: number | null;
  socketSentTimestamp: number | null;
  lastSkipReason: string | null;
  lastError: string | null;
  lastFrameBytes: number | null;
  lastFrameWidth: number | null;
  lastFrameHeight: number | null;
  lastFrameThumbnail: string | null; // Data URL for UI thumbnail
  lastAgentAnswer: string | null;
  recentEvents: VisualCaptureEvent[];
  // Full-360 capture diagnostic fields
  full360CaptureEnabled: boolean;
  lastFull360CaptureTime: number | null;
  lastFull360FrameBytes: number | null;
  lastFull360CaptureMs: number | null;
}

class VisualCaptureDiagnosticStore {
  private state: VisualCaptureDiagnosticState = {
    isDebugEnabled: false,
    selectedSourceId: 'none',
    activeModelId: null,
    sourceStatus: 'idle',
    dbCaptureEnabled: false,
    forceMode: 'auto',
    effectiveCaptureEnabled: false,
    requestTimestamp: null,
    capturedTimestamp: null,
    submittedTimestamp: null,
    socketSentTimestamp: null,
    lastSkipReason: null,
    lastError: null,
    lastFrameBytes: null,
    lastFrameWidth: null,
    lastFrameHeight: null,
    lastFrameThumbnail: null,
    lastAgentAnswer: null,
    recentEvents: [],
    // Full-360 capture diagnostic fields
    full360CaptureEnabled: false,
    lastFull360CaptureTime: null,
    lastFull360FrameBytes: null,
    lastFull360CaptureMs: null,
  };

  private latestFullJpegBase64: string | null = null;
  private listeners = new Set<() => void>();

  constructor() {
    if (typeof window !== 'undefined') {
      const isDev = process.env.NODE_ENV === 'development';
      const params = new URLSearchParams(window.location.search);
      const isDebugParam = params.get('visualCaptureDebug') === '1';
      this.state.isDebugEnabled = isDev && isDebugParam;

      const forceParam = params.get('visualCaptureForce');
      if (forceParam === '0') {
        this.state.forceMode = 'force_off';
      }

      this.updateEffectiveEnabled();

      (window as any).__visualCaptureDiagnostic = this;
    }
  }

  public isDebugActive(): boolean {
    return this.state.isDebugEnabled;
  }

  public subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  public getState(): VisualCaptureDiagnosticState {
    return this.state;
  }

  private notify(): void {
    for (const listener of this.listeners) {
      listener();
    }
  }

  private addEvent(event: VisualCaptureEvent): void {
    if (!this.state.recentEvents) {
      this.state.recentEvents = [];
    }
    this.state.recentEvents = [event, ...this.state.recentEvents.slice(0, 49)];
  }

  private updateEffectiveEnabled(): void {
    if (this.state.forceMode === 'force_off') {
      this.state.effectiveCaptureEnabled = false;
    } else {
      // Strictly follows the real Admin configuration (enable_visual_capture from DB)
      this.state.effectiveCaptureEnabled = this.state.dbCaptureEnabled;
    }
  }

  public setDbCaptureEnabled(enabled: boolean): void {
    this.state.dbCaptureEnabled = enabled;
    this.updateEffectiveEnabled();
    this.notify();
  }

  public setForceMode(mode: ForceCaptureMode): void {
    this.state.forceMode = mode;
    this.updateEffectiveEnabled();
    this.notify();
  }

  public isCaptureEnabled(dbEnabled: boolean): boolean {
    this.state.dbCaptureEnabled = dbEnabled;
    this.updateEffectiveEnabled();
    return this.state.effectiveCaptureEnabled;
  }

  public setSelectedSource(sourceId: 'three-model-viewer' | 'krpano' | 'none', modelId?: string | null): void {
    this.state.selectedSourceId = sourceId;
    if (modelId !== undefined) {
      this.state.activeModelId = modelId;
    }
    this.notify();
  }

  public setSourceStatus(status: VisualCaptureDiagnosticState['sourceStatus']): void {
    this.state.sourceStatus = status;
    this.notify();
  }

  public recordRequest(sourceId: 'three-model-viewer' | 'krpano' | 'none', modelId?: string | null): void {
    const now = Date.now();
    this.state.requestTimestamp = now;
    this.state.selectedSourceId = sourceId;
    if (modelId !== undefined) {
      this.state.activeModelId = modelId;
    }
    this.state.sourceStatus = 'capturing';

    this.addEvent({
      id: `req_${now}_${Math.random().toString(36).slice(2, 6)}`,
      timestamp: now,
      timeIso: new Date(now).toISOString(),
      stage: 'request',
      sourceId,
      modelId: modelId || this.state.activeModelId,
    });

    this.notify();
  }

  public recordCaptured(params: {
    sourceId: 'three-model-viewer' | 'krpano' | 'none';
    modelId?: string | null;
    width: number;
    height: number;
    encodedBytes: number;
    imageBase64: string;
  }): void {
    const now = Date.now();
    this.state.capturedTimestamp = now;
    this.state.lastFrameBytes = params.encodedBytes;
    this.state.lastFrameWidth = params.width;
    this.state.lastFrameHeight = params.height;
    this.state.sourceStatus = 'ready';

    // Store in-memory thumbnail for HUD
    const dataUrl = params.imageBase64.startsWith('data:')
      ? params.imageBase64
      : `data:image/jpeg;base64,${params.imageBase64}`;
    this.state.lastFrameThumbnail = dataUrl;
    this.latestFullJpegBase64 = dataUrl;

    this.addEvent({
      id: `cap_${now}_${Math.random().toString(36).slice(2, 6)}`,
      timestamp: now,
      timeIso: new Date(now).toISOString(),
      stage: 'captured',
      sourceId: params.sourceId,
      modelId: params.modelId || this.state.activeModelId,
      width: params.width,
      height: params.height,
      encodedBytes: params.encodedBytes,
    });

    this.notify();
  }

  public recordSubmittedToClient(params: {
    sourceId: 'three-model-viewer' | 'krpano' | 'none';
    modelId?: string | null;
    encodedBytes: number;
    clientStatus?: string;
  }): void {
    const now = Date.now();
    this.state.submittedTimestamp = now;

    this.addEvent({
      id: `sub_${now}_${Math.random().toString(36).slice(2, 6)}`,
      timestamp: now,
      timeIso: new Date(now).toISOString(),
      stage: 'submitted_to_client',
      sourceId: params.sourceId,
      modelId: params.modelId || this.state.activeModelId,
      encodedBytes: params.encodedBytes,
      clientStatus: params.clientStatus,
    });

    this.notify();
  }

  public recordSocketSent(params: {
    encodedBytes: number;
    mimeType: string;
  }): void {
    const now = Date.now();
    this.state.socketSentTimestamp = now;

    this.addEvent({
      id: `sok_${now}_${Math.random().toString(36).slice(2, 6)}`,
      timestamp: now,
      timeIso: new Date(now).toISOString(),
      stage: 'socket_sent',
      sourceId: this.state.selectedSourceId,
      modelId: this.state.activeModelId,
      encodedBytes: params.encodedBytes,
      details: { mimeType: params.mimeType },
    });

    this.notify();
  }

  public recordSkipped(params: {
    sourceId: 'three-model-viewer' | 'krpano' | 'none';
    modelId?: string | null;
    reason: string;
  }): void {
    const now = Date.now();
    this.state.lastSkipReason = params.reason;
    this.state.sourceStatus = 'skipped';

    this.addEvent({
      id: `skp_${now}_${Math.random().toString(36).slice(2, 6)}`,
      timestamp: now,
      timeIso: new Date(now).toISOString(),
      stage: 'skipped',
      sourceId: params.sourceId,
      modelId: params.modelId || this.state.activeModelId,
      reason: params.reason,
    });

    this.notify();
  }

  public recordError(params: {
    sourceId: 'three-model-viewer' | 'krpano' | 'none';
    modelId?: string | null;
    error: string;
  }): void {
    const now = Date.now();
    this.state.lastError = params.error;
    this.state.sourceStatus = 'error';

    this.addEvent({
      id: `err_${now}_${Math.random().toString(36).slice(2, 6)}`,
      timestamp: now,
      timeIso: new Date(now).toISOString(),
      stage: 'error',
      sourceId: params.sourceId,
      modelId: params.modelId || this.state.activeModelId,
      reason: params.error,
    });

    this.notify();
  }

  public recordAgentResponse(text: string): void {
    if (!this.state.lastAgentAnswer) {
      this.state.lastAgentAnswer = text;
    } else {
      this.state.lastAgentAnswer += text;
    }
    this.notify();
  }

  /**
   * Record a full-360 capture event for diagnostic purposes.
   */
  public recordFull360Capture(params: {
    enabled: boolean;
    captureTimeMs?: number;
    encodedBytes?: number;
  }): void {
    this.state.full360CaptureEnabled = params.enabled;
    if (params.captureTimeMs !== undefined) {
      this.state.lastFull360CaptureTime = Date.now();
      this.state.lastFull360CaptureMs = params.captureTimeMs;
    }
    if (params.encodedBytes !== undefined) {
      this.state.lastFull360FrameBytes = params.encodedBytes;
    }
    this.notify();
  }

  public getEventsWithoutPayloads(): VisualCaptureEvent[] {
    // Strictly sanitized: never outputs base64, audio, or secrets
    return this.state.recentEvents.map((evt) => ({
      id: evt.id,
      timestamp: evt.timestamp,
      timeIso: evt.timeIso,
      stage: evt.stage,
      sourceId: evt.sourceId,
      modelId: evt.modelId,
      reason: evt.reason,
      encodedBytes: evt.encodedBytes,
      width: evt.width,
      height: evt.height,
      clientStatus: evt.clientStatus,
      details: evt.details,
    }));
  }

  public downloadJpegFrame(filename = 'model-frame.jpg'): void {
    if (!this.latestFullJpegBase64 || typeof window === 'undefined') {
      alert('Chưa có frame nào được chụp để tải.');
      return;
    }

    try {
      const link = document.createElement('a');
      link.href = this.latestFullJpegBase64;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('[VisualCaptureDiagnostic] Failed to download JPEG frame:', err);
    }
  }

  public downloadEventsJson(filename = 'capture-events.json'): void {
    if (typeof window === 'undefined') return;

    try {
      const sanitizedEvents = this.getEventsWithoutPayloads();
      const exportData = {
        exportedAt: new Date().toISOString(),
        totalEvents: sanitizedEvents.length,
        state: {
          selectedSourceId: this.state.selectedSourceId,
          activeModelId: this.state.activeModelId,
          effectiveCaptureEnabled: this.state.effectiveCaptureEnabled,
          forceMode: this.state.forceMode,
          lastSkipReason: this.state.lastSkipReason,
          lastAgentAnswer: this.state.lastAgentAnswer,
        },
        events: sanitizedEvents,
      };

      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('[VisualCaptureDiagnostic] Failed to download events JSON:', err);
    }
  }

  public clearEvents(): void {
    this.state.recentEvents = [];
    this.state.requestTimestamp = null;
    this.state.capturedTimestamp = null;
    this.state.submittedTimestamp = null;
    this.state.socketSentTimestamp = null;
    this.state.lastSkipReason = null;
    this.state.lastError = null;
    this.state.lastAgentAnswer = null;
    this.notify();
  }
}

export const visualCaptureDiagnostic = new VisualCaptureDiagnosticStore();

export function isVisualCaptureDebugActive(): boolean {
  return visualCaptureDiagnostic.isDebugActive();
}
